import React, { useCallback, useMemo, useState } from 'react';
import { Container, Graphics, Sprite } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import { getGameImages } from '../../../core/helpers/getGameImages';
import GambleButton from './gambleButton';
import { slotSpineKey } from '../../../core/constants/symbolsNames';
import GambleCard from './gambleCard';
import GambleText from './gambleText';
import GambleAmount from './gambleAmount';
import GambleHistory from './gambleHistory';
import { gambleStore } from '../../../store/gambleStore';
import { gameService } from '../../../services/gameService';
import { GAMBLE_CARDS } from '../../../core/constants/gameConstants';
import { isMobile } from '../../../core/helpers/isMobile';
import Collect from './collect';
import { applicationStore } from '../../../store/applicationStore';
import { userInfoStore } from '../../../store/userInfoStore';

const Gamble = observer(() => {
  const [isInitAnimation, setIsInitAnimation] = useState(true);
  const {
    isShowGamblePopup,
    gambleInfo,
    openedCard,
    gambleHistoryCards,
    isAllowDoGambleAction,
    isGambleLose,
    setIsAllowDoGambleAction,
  } = gambleStore;

  const { gambleAttemptsCount, gambleAmountsInfo } = useMemo(() => {
    return gambleInfo || {};
  }, [gambleInfo]);

  const { currentWin, expectedWin } = useMemo(() => {
    return gambleAmountsInfo?.[0] || {};
  }, [gambleAmountsInfo]);

  const {
    blackCardDefault,
    blackCardPressed,
    blackCardHover,
    redCardDefault,
    redCardPressed,
    redCardHover,
    gambleBackground,
    gambleAmountBg,
    gambleYouWonText,
  } = useMemo(() => {
    return getGameImages();
  }, []);

  const handleSelectGambleNextCard = useCallback(
    (card) => {
      if (!isAllowDoGambleAction) {
        return;
      }
      setIsInitAnimation(false);
      setIsAllowDoGambleAction(false);
      gameService.sendGambleNextCard(card);
    },
    [isAllowDoGambleAction, setIsAllowDoGambleAction]
  );

  const handleSendCollect = useCallback(() => {
    if (!isAllowDoGambleAction) {
      return;
    }
    setIsAllowDoGambleAction(false);
    gameService.doSpinCollect();
  }, [isAllowDoGambleAction, setIsAllowDoGambleAction]);

  // if (!isShowGamblePopup) {
  //   return null;
  // }

  return (
    <Container name='GambleContainer' visible={isShowGamblePopup}>
      <Graphics
        draw={(g) => {
          g.clear();
          g.beginFill(isMobile() ? '#120601' : 'black');
          isMobile() ? g.drawRect(0, 0, 540, 960) : g.drawRect(0, 0, 1440, 810);
          g.alpha = 0.8;
          g.interactive = true;
        }}
      />
      <Collect
        x={isMobile() ? 270 : 1251}
        y={isMobile() ? 819 : 711}
        handleClickButton={handleSendCollect}
      />
      <Container x={isMobile() ? 0 : 275} y={isMobile() ? 0 : 138}>
        <Sprite
          x={isMobile() ? 270 : 445}
          y={isMobile() ? 406 : 255}
          texture={gambleBackground}
          anchor={0.5}
          alpha={isMobile() ? 0.85 : 0.85}
        />
        <GambleHistory
          x={isMobile() ? -175 : 0}
          y={isMobile() ? 219 : 0}
          history={gambleHistoryCards}
          isInitAnimation={isInitAnimation}
        />
        <GambleAmount
          x={isMobile() ? -60 : 0}
          y={isMobile() ? 178 : 0}
          amountTextConfig={{
            x: 176,
            y: 191,
            text: applicationStore.canvasTranslationKeys.GAMBLE_AMOUNT_TEXT,
          }}
          amountConfig={{
            x: 176,
            y: isMobile() ? 231 : 246,
            text: currentWin || '',
          }}
          spriteConfig={{ x: 176, y: isMobile() ? 230 : 246 }}
          backgroundImage={gambleAmountBg}
        />
        <GambleButton
          x={isMobile() ? 131 : 213}
          y={isMobile() ? 490 : 377}
          spineKey={slotSpineKey.gambleSymbol}
          additionalImages={{
            buttonBgHovered: redCardHover,
            buttonBgDefault: redCardDefault,
            buttonBgPressed: redCardPressed,
          }}
          skinName='heart'
          animationName='idle'
          handleClickButton={() => {
            handleSelectGambleNextCard(GAMBLE_CARDS.Red);
          }}
        />
        <GambleCard
          x={isMobile() ? 276 : 445}
          y={isMobile() ? 378 : 312}
          spineKey={slotSpineKey.gambleCard}
          animationName='idle'
          openedCard={openedCard}
          isGambleLose={isGambleLose}
          youWinTexture={gambleYouWonText}
        />
        <GambleText
          x={isMobile() ? 274 : 445}
          y={isMobile() ? 538 : 475}
          text={`${
            gambleAttemptsCount
              ? `${applicationStore.canvasTranslationKeys.GAMBLE_ATTEMPTS_LEFT_TEXT} ${gambleAttemptsCount}`
              : applicationStore.canvasTranslationKeys.GAMBLE_ATTEMPTS_FINISHED_TEXT
          }`}
          style={{ _fill: '#FFFFFF', _align: 'center', fontSize: 20, fontWeight: 500 }}
        />
        <GambleAmount
          x={isMobile() ? -280 : 0}
          y={isMobile() ? 178 : 0}
          backgroundImage={gambleAmountBg}
          amountTextConfig={{
            x: 713,
            y: 191,
            text: applicationStore.canvasTranslationKeys.GAMBLE_TO_WIN_TEXT,
          }}
          amountConfig={{
            x: 713,
            y: isMobile() ? 231 : 246,
            text: expectedWin || '',
          }}
          spriteConfig={{ x: 713, y: isMobile() ? 230 : 246 }}
        />
        <GambleButton
          x={isMobile() ? 422 : 675}
          y={isMobile() ? 490 : 377}
          spineKey={slotSpineKey.gambleSymbol}
          additionalImages={{
            buttonBgHovered: blackCardHover,
            buttonBgDefault: blackCardDefault,
            buttonBgPressed: blackCardPressed,
          }}
          skinName='spider'
          animationName='idle'
          handleClickButton={() => {
            handleSelectGambleNextCard(GAMBLE_CARDS.Black);
          }}
        />
        <Graphics
          draw={(g) => {
            g.clear();
            g.beginFill('black');
            g.drawRect(0, 0, isMobile() ? 540 : 1440, isMobile() ? 960 : 860);
            g.alpha = 0;
            g.interactive = userInfoStore.isReplay;
            g.visible = userInfoStore.isReplay;
          }}
        />
      </Container>
    </Container>
  );
});
export default Gamble;
